// trace-viewer/src/behaviors/moveable.ts
// Direct imports
import { TraceBehavior } from "./tracebehavior";
import { MoveableData } from "../scenedata/behaviors/moveabledata";
import { DragToMove } from "../managers/DragToMove";

/**
 * Moveable behavior — enables drag-to-move interaction at runtime.
 *
 * When present on a SceneObject, the object can be dragged in the camera plane
 * and via a ground-plane circle for XZ movement. Drag is only active during
 * play mode; in the editor, the sandbox's PlanarDragService manages its own
 * DragToMove instance for the selected object.
 *
 * Only the selected object shows the ground circle (blue XZ disc), and only
 * while its axes allow X or Z: a Y-only Moveable has no circle. Grabbing any
 * moveable object selects it via RuntimeSelectionManager, which also triggers
 * the selection outline and click-to-deselect.
 */
export class Moveable extends TraceBehavior<MoveableData> {
    static readonly behaviorType = "Moveable" as const;

    private _dragToMove: DragToMove | null = null;
    private _registeredForSelection = false;
    private _removeSelectionRegistration: (() => void) | null = null;

    protected override onAttach(): void {
        const scene = this.sceneObject.transform.getScene();
        this._dragToMove = new DragToMove(this.sceneObject.transform, scene, {
            axes: this._resolved.axes,
            momentumDamping: this._resolved.momentumDamping,
            groundCircle: false,
            onGrab: () => {
                this.sceneObject.traceScene.getRuntimeSelectionManager()
                    .select(this.sceneObject);
            },
        });
    }

    protected override onPlayModeChanged(playing: boolean): void {
        if (this._dragToMove) {
            this._dragToMove.enabled = playing;
        }

        if (playing) {
            this._registerForSelection();
        } else {
            this._unregisterForSelection();
            this._dragToMove?.setOptions({ groundCircle: false });
        }
    }

    protected override onDetach(): void {
        this._unregisterForSelection();
        if (this._dragToMove) {
            this._dragToMove.dispose();
            this._dragToMove = null;
        }
    }

    protected override async applyCommit(): Promise<void> {
        if (this._dragToMove) {
            this._dragToMove.setOptions({
                axes: this._resolved.axes,
                momentumDamping: this._resolved.momentumDamping,
            });
        }
    }

    private _registerForSelection(): void {
        if (this._registeredForSelection) return;
        this._registeredForSelection = true;

        this._removeSelectionRegistration = this.sceneObject.traceScene.getRuntimeSelectionManager().register(
            this.sceneObject,
            {
                onSelected: () => {
                    // Ground circle doesn't render correctly in stereo and is
                    // useless without a ground plane to project onto — never
                    // show it in immersive XR.
                    const inXR = this.sceneObject.traceScene
                        .getEventSystem()?.isXRActive?.() ?? false;
                    if (inXR) return;
                    this._dragToMove?.setOptions({ groundCircle: true });
                },
                onDeselected: () => {
                    this._dragToMove?.setOptions({ groundCircle: false });
                },
            },
        );
    }

    private _unregisterForSelection(): void {
        if (!this._registeredForSelection) return;
        this._registeredForSelection = false;

        this._removeSelectionRegistration?.();
        this._removeSelectionRegistration = null;
    }
}
